import React, { useRef } from 'react';
import { isWeddingFunction, MAX_NAME, groupNote } from '../../utils/helpers';

// Desktop "Quick add": the grid a hundred guests are typed into.
//
// ONE LINE IS ONE HOUSEHOLD - surname, the head's name, an optional number,
// and "others in the family" as plain names, because that is what the event's
// data looks like (83% of guests are in a household and 95 of 100 members just
// repeat the head's surname). Group and Guest-is are set once, at the top, for
// the whole batch. The function sets this event really uses are one click.
// Enter jumps to the same box on the next line (adding one at the end), so a
// whole list is typed without the mouse. All logic is in useQuickAdd.
export default function QuickAddDesktop({ vm }) {
  const {
    quickRows, quickFunctions, quickSaving, quickProgress, filledCount, closeQuick, quickVisibility, setQuickVisibility,
    quickGroup, setQuickGroup, quickGroups, quickSurnames,
    quickPresets, applyPresetToAll, familyMembers,
    setQuickField, toggleQuickFunction, toggleQuickColumn, columnState, addQuickRows, removeQuickRow, saveQuick,
  } = vm.quick;
  // How many PEOPLE the grid holds - a line of four is four guests.
  const people = quickRows.reduce((n, r) => (String(r.GuestName).trim() || String(r.Surname).trim()
    ? n + 1 + familyMembers(r).length : n), 0);
  const gridRef = useRef(null);

  const focusCell = (row, col) => {
    const el = gridRef.current?.querySelector(`[data-q="${row}-${col}"]`);
    if (el) { el.focus(); el.select?.(); }
  };
  const onKey = (e, row, col) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (row + 1 >= quickRows.length) {
      addQuickRows(1);
      setTimeout(() => focusCell(row + 1, col), 0);
    } else focusCell(row + 1, col);
  };
  const cell = (r, i, field, col, extra) => (
    <input data-q={`${i}-${col}`} value={r[field]} disabled={quickSaving}
      onChange={e => setQuickField(r.key, field, e.target.value)} onKeyDown={e => onKey(e, i, col)} {...extra} />
  );

  return (
    <div className="card quick-add gx-quick" ref={gridRef}>
      <div className="quick-add-head">
        <div>
          <strong>Quick add guests</strong>
          <span className="muted" title="New lines copy the ticks above · ticking any function also ticks the Wedding"> · Enter = next line · new lines copy the ticks above</span>
          <span className="gx-seg vis-seg" style={{ marginLeft: 10 }}>
            {['Private', 'Common'].map(v => (
              <button key={v} type="button" className={quickVisibility === v ? 'on' : ''} disabled={quickSaving}
                title={v === 'Private' ? 'Only you and the Event Admin see these guests' : 'Every member of the event sees them'}
                onClick={() => setQuickVisibility(v)}>
                {v === 'Private' ? '🔒 Only me + admin' : '👁 All members'}
              </button>
            ))}
          </span>
        </div>
        <button type="button" className="icon-btn" title="Close quick add" onClick={closeQuick} disabled={quickSaving}>✕</button>
      </div>

      {/* Set once for the batch: nobody types a group a hundred times. */}
      <div className="quick-batch">
        <label className="field" title={groupNote(quickGroup) || 'The circle this batch belongs to'}>
          Group{groupNote(quickGroup) ? ` — ${groupNote(quickGroup)}` : ''}
          <input list="quick-groups" value={quickGroup} disabled={quickSaving} placeholder="none"
            onChange={e => setQuickGroup(e.target.value)} />
        </label>
        <datalist id="quick-groups">{quickGroups.map(g => <option key={g} value={g} />)}</datalist>

        {quickPresets.length > 0 && (
          <div className="quick-presets">
            <span className="muted">Invite everyone here to</span>
            {quickPresets.map(p => (
              <button key={p.key} type="button" className="btn light sm" disabled={quickSaving}
                title={`${p.label}${p.count ? ` — ${p.count} guests already have this set` : ''}`}
                onClick={() => applyPresetToAll(p.ids)}>
                {p.label}{p.count ? <span className="fn-count"> ({p.count})</span> : null}
              </button>
            ))}
          </div>
        )}
      </div>

      <datalist id="quick-surnames">{quickSurnames.map(sn => <option key={sn} value={sn} />)}</datalist>

      <div className="quick-add-scroll">
        <table className="tbl compact quick-add-table">
          <thead>
            <tr>
              <th className="num">#</th>
              <th>Surname</th>
              <th>Name *</th>
              <th>Mobile</th>
              <th title="The rest of this household — names only; they take the surname above. Add a number after a name when they have one.">
                Others in the family
              </th>
              {quickFunctions.map(f => {
                const state = columnState(f.FunctionId);
                return (
                  <th key={f.FunctionId} className="fn-col" title={`Tick / clear "${f.FunctionName}" for every line`}>
                    <label>
                      <input type="checkbox" checked={state === 'all'} disabled={quickSaving}
                        ref={el => { if (el) el.indeterminate = state === 'some'; }}
                        onChange={() => toggleQuickColumn(f.FunctionId)} />
                      <span>{isWeddingFunction(f) ? '💍 ' : ''}{f.FunctionName} <span className="fn-count">({vm.fnCount(f.FunctionId)})</span></span>
                    </label>
                  </th>
                );
              })}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {quickRows.map((r, i) => (
              <React.Fragment key={r.key}>
                <tr className={r.error ? 'has-error' : undefined}>
                  <td className="num muted">{i + 1}</td>
                  <td>{cell(r, i, 'Surname', 0, { placeholder: 'Surname', maxLength: MAX_NAME, autoFocus: i === 0, list: 'quick-surnames' })}</td>
                  <td>{cell(r, i, 'GuestName', 1, { placeholder: 'Name', maxLength: MAX_NAME })}</td>
                  <td>{cell(r, i, 'MobileNumber', 2, { placeholder: 'optional', inputMode: 'numeric', className: 'mobile' })}</td>
                  <td className="quick-fam">
                    {cell(r, i, 'Family', 3, { placeholder: 'Kamla, Ravi 9822011111', className: 'fam' })}
                    {familyMembers(r).length > 0 && (
                      <span className="chip ok" title={familyMembers(r).map(m => m.GuestName).join(', ')}>
                        +{familyMembers(r).length}
                      </span>
                    )}
                  </td>
                  {quickFunctions.map(f => (
                    <td key={f.FunctionId} className="fn-col">
                      <input type="checkbox" disabled={quickSaving} title={f.FunctionName}
                        checked={r.FunctionIds.includes(String(f.FunctionId))}
                        onChange={() => toggleQuickFunction(r.key, f.FunctionId)} />
                    </td>
                  ))}
                  <td>
                    <button type="button" className="icon-btn danger" title="Remove this line" tabIndex={-1}
                      disabled={quickSaving || quickRows.length === 1} onClick={() => removeQuickRow(r.key)}>✕</button>
                  </td>
                </tr>
                {r.error && (
                  <tr className="quick-add-error"><td></td><td colSpan={quickFunctions.length + 5}>⚠ {r.error}</td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="quick-add-foot">
        <button type="button" className="btn light sm" onClick={() => addQuickRows(1)} disabled={quickSaving}>+ 1 line</button>
        <button type="button" className="btn light sm" onClick={() => addQuickRows(5)} disabled={quickSaving}>+ 5 lines</button>
        <div className="spacer" />
        {quickSaving && <span className="muted">Saving {quickProgress.done} / {quickProgress.total}…</span>}
        {!quickSaving && filledCount > 0 && (
          <span className="muted">{people} {people === 1 ? 'guest' : 'guests'} in {filledCount} {filledCount === 1 ? 'household' : 'households'}</span>
        )}
        <button type="button" className="btn primary" data-save="primary" title="Ctrl+S" onClick={saveQuick} disabled={quickSaving || !filledCount}>
          {quickSaving ? 'Saving…' : `Save ${people || ''} guest${people === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}
